import {Checkbox} from '@material-ui/core';
import clsx from 'clsx';
import React, {ChangeEvent, ReactElement, useRef} from 'react';
import {Nil} from '../model/SharedTypes';
import {errorClassName} from '../nxGenericTextField/NxGenericTextField';
import styles from './NxCheckbox.scss';

export interface NxCheckboxProps {
  checked?: boolean | null;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  error?: string;
  name?: string;
  onBlur?: (e: ChangeEvent) => void;
  onChange?: (e: ChangeEvent) => void;
  positionAbsoluteError?: boolean,
  required?: boolean;
  translucentUncheckedRow?: boolean;
  dataTest?: string;
}

const calculateValue = (newChecked: boolean | Nil, previousChecked: boolean | Nil): boolean => {
  if (newChecked === null) {
    return previousChecked ?? false;
  }

  return !!newChecked;
}

const NxCheckbox = (
  {
    checked,
    children,
    className = '',
    disabled = false,
    error,
    name,
    onBlur,
    onChange,
    positionAbsoluteError = true,
    required,
    translucentUncheckedRow = false,
    dataTest
  }: NxCheckboxProps
): ReactElement => {

  const checkboxClasses = { root: styles.checkbox };
  const rowClassName = clsx(
    className,
    {
      [styles.row]: children,
      [styles.row_translucent]: children && translucentUncheckedRow && !checked
    }
  );

  const lastValue = useRef(checked);
  const finalChecked = calculateValue(checked, lastValue.current);
  lastValue.current = finalChecked;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    lastValue.current = event.target.checked;

    // All components log a warning when we don't provide onClick callback
    // Select component expects Checkbox onClick to bubble, so
    // not providing onClick is a valid scenario and we cannot guard against it
    if (!onChange) {
      return;
    }

    onChange(event);
  };

  return (
    <div className={styles.wrapper}>
      <div className={rowClassName}>
        <Checkbox classes={checkboxClasses}
                  checked={finalChecked}
                  disabled={disabled}
                  required={required}
                  onChange={handleChange}
                  onBlur={onBlur}
                  name={name}
                  inputProps={{ 'data-test': dataTest } as never }/>
        {children}{required && <span className={clsx({[styles.requiredMarkError]: error})}>*</span>}
      </div>
      {
        !!error && <div className={errorClassName(positionAbsoluteError)}>{error}</div>
      }
    </div>
  );
}

export default NxCheckbox;
